import { RowAction } from "@beeldit/core/enums/RowAction.enum";
import { TableAction } from "@beeldit/core/enums/TableAction.enum";
import { Pencil, Plus, Trash } from "@phosphor-icons/react";
import { t } from "i18next";
import CustomerSelector from "./components/CustomerSelector";
import PetSelector from "@main/pets/components/PetSelector";
import FeeSelector from "@main/fees/components/FeeSelector";
import GenderSelector from "@main/pets/components/GenderSelector";
import PetCustomerSelector from "@main/pets/components/PetCustomerSelector";
import PetArrayFieldTemplate from "./templates/PetArrayFieldTemplate";
import AsyncCustomerSelector from "./components/AsyncCustomerSelector";

const configCustomersAndPets = {
  modelName: "customer",
  modelPluralName: "customers",
  endpoint: "customers",
  tableConfig: {
    index: null,
    actions: [],
    rowActions: [
      {
        name: RowAction.EDIT,
        label: "edit",
        icon: <Pencil size={20} />,
        allowed: true,
        show: true,
        action: (element: any) => {
          console.log("edit", element);
        },
      },
      {
        name: RowAction.DELETE,
        label: "delete",
        icon: <Trash size={20} />,
        allowed: true,
        show: true,
        action: (element: any) => {
          console.log("delete", element);
        },
      },
    ],
    rowConfiguration: {
      class: (element: any) => {
        return null;
      },
    },
    columns: [
      {
        name: "id",
        label: "#",
        key: "id",
        type: "number",
        show: true,
        allowOrder: true,
      },
      {
        name: "name",
        label: "name",
        key: "name",
        type: "string",
        show: true,
        allowOrder: true,
      },
      {
        name: "surnames",
        label: "surnames",
        key: "surnames",
        type: "string",
        show: true,
        allowOrder: true,
      },
      {
        name: "nif",
        label: "nif",
        key: "nif",
        type: "string",
        show: true,
        allowOrder: true,
      },
      {
        name: "email",
        label: "email",
        key: "email",
        type: "string",
        show: true,
        allowOrder: true,
      },
    ],
    pagination: {
      enabled: true,
      back: true,
    },
  },
  filterConfig: {
    type: "object",
    properties: {
      customer_id: {
        type: "number",
      },
      pet_id: {
        type: "number",
      },
    },
  },
  filterUIConfig: {
    uiScheme: {
      className: "filters-container",
      search: {
        "ui:options": {
          label: false, // Oculta el título del campo
          placeholder: "Buscar...",
        },
      },
      customer_id: {
        "ui:options": {
          label: false,
          placeholder: "Buscar por cliente",
        },
        "ui:widget": "customer-selector",
      },
      pet_id: {
        "ui:options": {
          label: false,
          placeholder: "Buscar por mascota",
        },
        "ui:widget": "pet-selector",
      },
    },
    widgets: {
      "customer-selector": CustomerSelector,
      "pet-selector": PetCustomerSelector,
    },
  },
  formSchema: {
    type: "object",
    title: t("customer_data"),
    properties: {
      // Name
      name: {
        type: "string",
        title: t("name"),
      },
      // Surnames
      surnames: {
        type: "string",
        title: t("surnames"),
      },
      // Phone
      phone: {
        type: "string",
        title: t("phone"),
      },
      // Mobile
      mobile: {
        type: ["string", "null"],
        title: t("mobile"),
      },
      // Email
      email: {
        type: ["string", "null"],
        title: t("email"),
      },
      // NIF
      nif: {
        type: "string",
        title: t("nif"),
      },
      // Address
      address: {
        type: ["string", "null"],
        title: t("address"),
      },
      // City
      city: {
        type: ["string", "null"],
        title: t("city"),
      },
      // State
      state: {
        type: ["string", "null"],
        title: t("state"),
      },
      // Postal code
      postal_code: {
        type: ["string", "null"],
        title: t("postal_code"),
      },
      // Observations
      observations: {
        type: ["string", "null"],
        title: t("observations"),
      },
      // Pets
      pets: {
        type: "array",
        title: t("pets"),
        items: {
          type: "object",
          properties: {
            // Name
            name: {
              type: "string",
              title: t("name"),
            },
            // Breed
            breed: {
              type: "string",
              title: t("breed"),
            },
            // Fee_id
            fee_id: {
              type: "number",
              title: t("fee_id"),
            },
            gender: {
              type: "string",
              title: t("gender"),
            },
            feed: {
              type: ["string", "null"],
              title: t("feed"),
            },
            // Eat:
            eat: {
              type: ["number", "null"],
              title: t("eat"),
            },
            // Eat quantity
            eat_quantity: {
              type: ["number", "null"],
              title: t("eat_quantity"),
            },
            // Disease
            disease: {
              type: ["string", "null"],
              title: t("disease"),
            },
            // Passport
            passport: {
              type: ["string", "null"],
              title: t("passport"),
            },
            // Microchip
            microchip: {
              type: ["string", "null"],
              title: t("microchip"),
            },
            // Description
            description: {
              type: ["string", "null"],
              title: t("description"),
            },
            // Observations
            observations: {
              type: ["string", "null"],
              title: t("observations"),
            },
          },
        },
      },
    },
  },
  uiFormSchema: {
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    observations: {
      "ui:widget": "textarea",
    },
    colsSchema: [
      {
        cols: "col-5",
        elements: [
          [
            { name: "name", cols: "col-12" },
            { name: "surnames", cols: "col-12" },
            { name: "phone", cols: "col-6" },
            { name: "mobile", cols: "col-6" },
            { name: "email", cols: "col-6" },
            { name: "nif", cols: "col-6" },
            { name: "address", cols: "col-12" },
            { name: "city", cols: "col-6" },
            { name: "state", cols: "col-6" },
            { name: "postal_code", cols: "col-6" },
            { name: "observations", cols: "col-12" },
          ],
        ],
      },
      {
        cols: "col-7",
        elements: [[{ name: "pets", cols: "col-12" }]],
      },
    ],
    pets: {
      "ui:ArrayFieldTemplate": PetArrayFieldTemplate,
      items: {
        gender: {
          "ui:widget": "gender-selector",
        },
        customer_id: {
          "ui:widget": "customer-selector",
        },
        fee_id: {
          "ui:widget": "fee-selector",
        },
        description: {
          "ui:widget": "textarea",
        },
        observations: {
          "ui:widget": "textarea",
        },
        colsSchema: [
          {
            cols: "col-12",
            elements: [
              [
                {
                  name: "name",
                  cols: "col-6",
                },
                {
                  name: "breed",
                  cols: "col-6",
                },
              ],
              [
                {
                  name: "fee_id",
                  cols: "col-6",
                },
                {
                  name: "gender",
                  cols: "col-6",
                },
                {
                  name: "feed",
                  cols: "col-12",
                },
                {
                  name: "eat",
                  cols: "col-6",
                },
                {
                  name: "eat_quantity",
                  cols: "col-6",
                },
                {
                  name: "disease",
                  cols: "col-12",
                },
                {
                  name: "passport",
                  cols: "col-6",
                },
                {
                  name: "microchip",
                  cols: "col-6",
                },
                {
                  name: "description",
                  cols: "col-12",
                },
                {
                  name: "observations",
                  cols: "col-12",
                },
              ],
            ],
          },
        ],
      },
    },
  },
  formWidgets: {
    "fee-selector": FeeSelector,
    "gender-selector": GenderSelector,
  },
};
export default configCustomersAndPets;
